/**
 * @fileoverview Zod schemas for organizations with tolerant parsing and branded IDs.
 * @module features/orgs/schemas/orgSchemas
 */

import { z } from 'zod'

/** Branded type for organization ID. */
export type OrgId = z.infer<typeof OrgIdSchema>

/**
 * Tolerant OrgId schema: accepts uuid | string | number and normalizes to string, then brands.
 */
export const OrgIdSchema = z
  .union([z.string(), z.number()])
  .transform(v => String(v))
  .pipe(z.string().brand<'OrgId'>())

/** Organization domain type. */
export type Org = z.infer<typeof OrgSchema>

/**
 * Organization schema with tolerant id.
 */
export const OrgSchema = z.object({
  id: OrgIdSchema,
  name: z.string().min(1),
  role: z.string().optional(),
})

/** Variants for listing organizations across common backend shapes. */
export const OrgsArraySchema = z.array(OrgSchema)
export const OrgsEnvelopeOrganizations = z.object({ organizations: OrgsArraySchema })
export const OrgsEnvelopeContent = z.object({ content: OrgsArraySchema })
export const OrgsEnvelopeData = z.object({ data: OrgsArraySchema })

/**
 * Profile schema returned by /auth/me and switch-org with preferences.
 */
export const ProfileSchema = z.object({
  organizations: OrgsArraySchema,
  preferredLanguage: z.string().min(1).optional(),
  defaultOrgId: OrgIdSchema.optional(),
})
export type Profile = z.infer<typeof ProfileSchema>

/** Auth profile fallback shape for /auth/me (legacy minimal). */
export const AuthMeSchema = ProfileSchema

/**
 * Organization settings schema.
 */
export const OrgSettingsSchema = z.object({
  reportingCurrency: z.string(),
  allowedCurrencies: z.array(z.string()).default([]),
})
export type OrgSettings = z.infer<typeof OrgSettingsSchema>
